import {
  BuildingOffice2Icon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { useAlertView, useConfirmationView } from '~/app';
import { Button, LoadingSpinner, Tabs } from '~/components';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import {
  declineResponseConfirmation,
  withdrawResponseConfirmation,
} from '~/features/shared/responseCloseConfirmations';
import { sendSuccessAlert } from '~/features/utils';
import { useCustomForm } from '~/hooks/useCustomForm';
import { b2bSideCopy, type ListingSide } from '../b2bSideCopy';
import { ContractReadyNotice } from '../contracts/ContractReadyNotice';
import { ListingFilledNotice } from '../job-opportunities/sub-components/ListingFilledNotice';
import {
  FEE_AUTHORIZATION_REQUIRED_MESSAGE,
  FEE_SCHEDULE_CHANGED_MESSAGE,
} from '../listing-search/errors';
import { useNegotiationRef } from '../marketSelectors';
import { useB2BAcceptFee } from '../useB2BAcceptFee';
import { TABS } from './consts';
import { NegotiationEditingContext, NegotiationRefContext } from './provider';
import { ActivationFeeDialog } from './sub-components/ActivationFeeDialog';
import { Compensation } from './tabs/Compensation/Compensation';
import { ContractOverview } from './tabs/ContractOverview';
import { ContractTerms } from './tabs/ContractTerms';
import { ShiftDistribution } from './tabs/ShiftDistribution';
import {
  buildNegotiationSchema,
  DraftSubmitResult,
  NegotiationDraftSource,
  NegotiationFormValues,
  NegotiationParty,
  NegotiationResponseType,
  NegotiationSchema,
} from './types';
import {
  getMsaasNegotiationViewerSide,
  getNegotiationListingOwnerName,
  getNegotiationListingTitle,
  getNegotiationResponderName,
  getNegotiationTurnLabel,
  getNegotiationViewerRole,
  getNegotiationViewMode,
  getShiftAllocationError,
  isNegotiationAwaitingViewer,
  isSameNegotiationDraftSource,
} from './utils';

const TAGS = CT.employer.negotiation;
// [MCV-1646] `submitOfferBtn` does not exist in @marketplace-ai/tags
// (libs/cy-tags, outside apps/mcv-client -- see PR spec deviation note) and
// TAGS.formBtns has no slot for it, so this stays a literal string.
const SUBMIT_OFFER_BTN_CY_TAG = 'negotiation-submit-offer-btn';

type NegotiationDetailsProps = {
  footer?: React.ReactNode;
  responseId?: number;
  responseType?: NegotiationResponseType;
  viewerRole?: NegotiationParty;
  onClose?: () => void;
  // [MCV-1646] draft-first initial offer.
  draftSource?: { listingType: 'JOB' | 'MSAAS'; listingId: number };
  draftProviderIds?: number[];
  startDraftIfEmpty?: boolean;
  onDraftSubmitted?: (result: DraftSubmitResult) => void;
  // Overrides the first-offer button copy (MCV-1889: a request for labor "Send request").
  submitLabels?: { submit: string; submitting: string; retry: string };
  // [MCV-1889] Which side the listing is on, for the party strip, and whether the response closed
  // because the listing filled, which replaces the actions with a note.
  listingSide?: ListingSide | null;
  listingFilled?: boolean;
};

const parseResponseType = (value: string | null): NegotiationResponseType =>
  value === 'job' ? 'job' : 'msaas';

export const NegotiationDetails = ({
  footer,
  responseId,
  responseType,
  viewerRole,
  onClose,
  draftSource,
  draftProviderIds,
  startDraftIfEmpty,
  onDraftSubmitted,
  submitLabels,
  listingSide,
  listingFilled = false,
}: NegotiationDetailsProps) => {
  // [MCV-1557] `loading` from useNegotiationRef() is the top-level app/user-
  // bootstrap flag (selectMarketState -> UserContext), not this negotiation's
  // own fetch state. It stays false while a by-response fetch is genuinely in
  // flight, so gating the spinner on it alone let the spinner resolve before
  // data arrived. Keep it only for the (rare) case this component renders
  // before the bootstrap machine has spawned marketRef/negotiationRef at all;
  // the negotiation actor's own context.loading (below) drives the fetch spinner.
  const { loading: bootstrapLoading, negotiationRef, employerUser } = useNegotiationRef();
  const [numChangedFields, setNumChangedFields] = useState(0);
  const [formReset, setFormReset] = useState(false);
  // The values the last accept was sent with, so a refused fee schedule can be re-offered.
  const lastAcceptValuesRef = useRef<NegotiationFormValues | null>(null);
  const [searchParams] = useSearchParams();
  const alertView = useAlertView();
  const confirmationView = useConfirmationView();

  const negotiation = useSelector(negotiationRef, (state) => state?.context.negotiation);
  const machineDraft = useSelector(negotiationRef, (state) => state?.context.draft);
  const machineDraftSource = useSelector(negotiationRef, (state) => state?.context.draftSource);
  const submitting = useSelector(negotiationRef, (state) => state?.context.submitting ?? false);
  const submitLifecycleBusy = useSelector(
    negotiationRef,
    (state) => state?.context.submitLifecycleBusy ?? false
  );
  const submitResult = useSelector(negotiationRef, (state) => state?.context.submitResult);
  const machineUncertainSubmission = useSelector(
    negotiationRef,
    (state) => !!state?.context.submitError?.uncertain
  );
  const submittedDraft = useSelector(negotiationRef, (state) => state?.context.submittedDraft);
  const machineEmployerUser = useSelector(negotiationRef, (state) => state?.context.employerUser);
  const submitResultSource = useSelector(
    negotiationRef,
    (state) => state?.context.submitResultSource
  );
  const counterResult = useSelector(negotiationRef, (state) => state?.context.counterResult);
  const acceptRefusal = useSelector(negotiationRef, (state) => state?.context.acceptRefusal);
  // [MCV-1557] The negotiation machine's OWN loading flag (set/cleared around
  // its $_LOAD invoke in negotiation.machine.ts), as opposed to the unrelated
  // app-bootstrap `loading` above.
  const negotiationLoading = useSelector(
    negotiationRef,
    (state) => state?.context.loading ?? false
  );
  // [MCV-1557] Load failed, as opposed to succeeding with an empty chain.
  const negotiationLoadFailed = useSelector(
    negotiationRef,
    (state) => state?.context.loadFailed ?? false
  );
  const tabIndex = useSelector(negotiationRef, (state) => state?.context.tabIndex ?? 0);
  const latestVersion = useSelector(negotiationRef, (state) => state?.context.latestVersion ?? 0);
  const isReadOnly = useSelector(negotiationRef, (state) => state?.context.isReadOnly ?? false);
  const provider = useSelector(negotiationRef, (state) => state?.context.provider ?? null);
  const machineResponseId = useSelector(negotiationRef, (state) => state?.context.responseId);
  const machineResponseType = useSelector(
    negotiationRef,
    (state) => state?.context.responseType ?? 'msaas'
  );
  const machineViewerRole = useSelector(negotiationRef, (state) => state?.context.viewerRole);
  const machineStartDraftIfEmpty = useSelector(
    negotiationRef,
    (state) => state?.context.startDraftIfEmpty ?? false
  );
  const currentViewerRole = getNegotiationViewerRole({ employerUser, provider, viewerRole });

  const responseIdParam = searchParams.get('responseId');
  const parsedResponseId = useMemo(() => {
    if (responseId != null) return responseId;
    if (!responseIdParam) return undefined;
    const n = Number(responseIdParam);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [responseId, responseIdParam]);
  // [MCV-1646] A bare-listing draftSource carries its own listingType, which
  // is the only signal of JOB vs. MSaaS a caller like ProviderJobListings has
  // (it never passes `responseType` explicitly). Falling back to the 'msaas'
  // default there would build the wrong zod schema (e.g. requiring the
  // shift-allocation rule for a JOB draft) and mislabel the header.
  const parsedResponseType =
    responseType ??
    (draftSource
      ? draftSource.listingType === 'JOB'
        ? 'job'
        : 'msaas'
      : parseResponseType(searchParams.get('responseType')));
  // [MCV-1850] The listing owner declines an offer; the responder withdraws. A JOB listing's
  // owner is its employer, but B2B has an employer on both sides, so that is read off the
  // negotiation. Not matched to either side means the editor is read-only and neither shows.
  const viewerIsListingOwner =
    parsedResponseType === 'job'
      ? currentViewerRole === 'EMPLOYER'
      : negotiation != null &&
        getMsaasNegotiationViewerSide(negotiation, {
          employerUser,
          provider,
          viewerRole: currentViewerRole,
        }) === 'LISTING_OWNER';
  const currentDraftSource = useMemo<NegotiationDraftSource | undefined>(
    () =>
      parsedResponseId !== undefined
        ? { responseId: parsedResponseId }
        : draftSource
          ? { listingType: draftSource.listingType, listingId: draftSource.listingId }
          : undefined,
    [parsedResponseId, draftSource?.listingId, draftSource?.listingType]
  );
  const responseScopeMatches =
    parsedResponseId !== undefined &&
    machineResponseId === parsedResponseId &&
    machineResponseType === parsedResponseType &&
    machineViewerRole === currentViewerRole;
  const draftSourceMatches = isSameNegotiationDraftSource(machineDraftSource, currentDraftSource);
  const employerScopeMatches =
    machineEmployerUser?.id === employerUser?.id &&
    machineEmployerUser?.employerId === employerUser?.employerId;
  // A listing draft can discover an existing response. Its uncertain retry must
  // keep the original listing source/payload when reopened by that response ID.
  const retainedSubmissionMatchesResponse =
    responseScopeMatches &&
    employerScopeMatches &&
    machineDraftSource !== undefined &&
    'listingId' in machineDraftSource &&
    (machineUncertainSubmission || (submitLifecycleBusy && submittedDraft !== undefined));
  const draftScopeMatches = draftSourceMatches || retainedSubmissionMatchesResponse;
  const draft = draftScopeMatches ? machineDraft : undefined;
  const uncertainSubmission = draftScopeMatches && machineUncertainSubmission;

  // [MCV-1557] One dispatch per mounted instance per scope. This is what makes
  // the retry below safe: `loadFailed` is deliberately NOT an effect dependency,
  // because a failing fetch would then re-trigger the effect, which would
  // re-dispatch, which would fail again -- an infinite retry loop against a
  // broken endpoint. A ref scoped to the component instance gives exactly one
  // attempt per open, and reopening mounts a fresh instance with a fresh ref.
  const requestedScopeRef = useRef<string | null>(null);

  // Dispatch ON_LOAD (by responseId) or ON_OPEN_DRAFT (by bare listing) when
  // this component is rendered as a standalone route or embedded in a modal
  // whose parent has not already loaded this exact scope.
  useEffect(() => {
    if (!negotiationRef) return;
    // A submit owns the singleton actor through its authoritative reload. Do
    // not mark a new scope as requested while it is busy: once the lifecycle
    // settles this effect reruns and the new listing/response gets its load.
    if (submitLifecycleBusy) return;

    if (parsedResponseId === undefined) {
      if (!draftSource) return;
      const scopeKey = `draft|${draftSource.listingType}|${draftSource.listingId}`;
      if (requestedScopeRef.current === scopeKey) return;
      requestedScopeRef.current = scopeKey;
      negotiationRef.send({
        type: 'ON_OPEN_DRAFT',
        payload: {
          employerUser,
          viewerRole: currentViewerRole,
          listingType: draftSource.listingType,
          listingId: draftSource.listingId,
          providerIds: draftProviderIds,
        },
      });
      return;
    }

    const scopeKey = `${parsedResponseId}|${parsedResponseType}|${currentViewerRole}|${Boolean(startDraftIfEmpty)}`;
    if (requestedScopeRef.current === scopeKey) return;

    // [MCV-1557] Adopting the parent's existing load for this scope is right
    // when it worked (or is still in flight) -- but NOT when it failed. The
    // machine keeps responseId/responseType/viewerRole after a failure, so
    // without the loadFailed check, closing and reopening the SAME response
    // matched here, skipped ON_LOAD entirely, and re-rendered the failure with
    // no request ever being made. The error state tells the user to reopen to
    // try again, so reopening has to actually try again.
    // Matching response metadata alone cannot adopt a listing-shaped draft:
    // ordinary drafts reload in the requested scope; uncertain ones reconcile
    // that response while the machine retains their original retry source.
    const needsDraftFallbackLoad =
      startDraftIfEmpty &&
      !machineDraft &&
      !negotiation &&
      !(negotiationLoading && machineStartDraftIfEmpty);
    if (
      responseScopeMatches &&
      !negotiationLoadFailed &&
      (!machineDraft || draftSourceMatches) &&
      !needsDraftFallbackLoad
    ) {
      requestedScopeRef.current = scopeKey;
      return;
    }

    requestedScopeRef.current = scopeKey;
    negotiationRef.send({
      type: 'ON_LOAD',
      payload: {
        employerUser,
        responseId: parsedResponseId,
        responseType: parsedResponseType,
        viewerRole: currentViewerRole,
        startDraftIfEmpty,
      },
    });
  }, [
    negotiationRef,
    parsedResponseId,
    parsedResponseType,
    machineResponseId,
    machineResponseType,
    machineViewerRole,
    machineStartDraftIfEmpty,
    machineDraft,
    negotiation,
    negotiationLoading,
    draftSourceMatches,
    responseScopeMatches,
    employerUser,
    currentViewerRole,
    draftSource,
    draftProviderIds,
    startDraftIfEmpty,
    negotiationLoadFailed,
    submitLifecycleBusy,
  ]);

  // Notify the caller exactly once per successful submit. This actor is a
  // singleton, so a result left behind by a closed modal must never be handed
  // to the next listing/response that mounts. Every result carries its source
  // scope and is explicitly consumed after this effect observes it.
  const notifiedNegotiationIdRef = useRef<number | null>(null);
  // [MCV-1816] Auto-close and the success toast are fired from effects, so they
  // read the latest callbacks through refs instead of re-running on every parent
  // render (the alert view and onClose are not referentially stable).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const alertViewRef = useRef(alertView);
  alertViewRef.current = alertView;
  const mountedAtRef = useRef(Date.now());
  useEffect(() => {
    if (!submitResult) return;
    if (notifiedNegotiationIdRef.current === submitResult.negotiationId) return;
    notifiedNegotiationIdRef.current = submitResult.negotiationId;
    try {
      if (
        isSameNegotiationDraftSource(submitResultSource, currentDraftSource) ||
        (submitResultSource !== undefined &&
          'listingId' in submitResultSource &&
          parsedResponseId !== undefined &&
          submitResult.jobResponseId === parsedResponseId &&
          submitResult.responseType === parsedResponseType &&
          employerScopeMatches &&
          machineViewerRole === currentViewerRole)
      ) {
        onDraftSubmitted?.(submitResult);
        // [MCV-1816] The first offer is sent to the other party, so the popup
        // closes once the parent has handled the result. `onClose` is not gated
        // on the post-submit reload still being in flight (the Close button is).
        alertViewRef.current.send(
          sendSuccessAlert({ text: 'Offer sent', subText: 'The other party has been notified.' })
        );
        onCloseRef.current?.();
      }
    } finally {
      negotiationRef?.send({
        type: 'ON_CONSUME_SUBMIT_RESULT',
        payload: { negotiationId: submitResult.negotiationId },
      });
    }
  }, [
    currentDraftSource,
    negotiationRef,
    onDraftSubmitted,
    submitResult,
    submitResultSource,
    parsedResponseId,
    parsedResponseType,
    machineViewerRole,
    currentViewerRole,
    employerScopeMatches,
  ]);

  // [MCV-1816] A successful counter goes back to the other party, who is
  // notified, so the sender's popup closes. The machine is a singleton: the
  // result is consumed here, and one set before this instance mounted (a counter
  // that finished after its popup was already closed) is dropped silently.
  useEffect(() => {
    if (!counterResult) return;
    const isFresh = counterResult.at >= mountedAtRef.current;
    const isThisResponse =
      counterResult.responseId === undefined ||
      counterResult.responseId === (parsedResponseId ?? machineResponseId);
    negotiationRef?.send({
      type: 'ON_CONSUME_COUNTER_RESULT',
      payload: { at: counterResult.at },
    });
    if (!isFresh || !isThisResponse) return;
    alertViewRef.current.send(
      sendSuccessAlert({
        text:
          counterResult.kind === 'decline'
            ? 'Response declined'
            : counterResult.kind === 'withdraw'
              ? 'Response withdrawn'
              : 'Counter offer sent',
        subText: 'The other party has been notified.',
      })
    );
    onCloseRef.current?.();
  }, [counterResult, negotiationRef, parsedResponseId, machineResponseId]);

  // [MCV-1858] The server refused the accept for its platform-fee schedule (428 not authorized,
  // 409 changed since the preview). Refetch the schedule and show it again with the reason. Like
  // the counter result above, one set before this instance mounted is dropped silently.
  // Assigned where the handlers are defined, below the loading/error early returns.
  const sendAccept = useCallback(
    (values: NegotiationFormValues, feeAuthorization?: FeeAuthorization) => {
      lastAcceptValuesRef.current = values;
      negotiationRef?.send({
        type: 'ON_ACCEPT_OFFER',
        data: values,
        ...(feeAuthorization ? { feeAuthorization } : {}),
      });
    },
    [negotiationRef]
  );
  const acceptFee = useB2BAcceptFee<NegotiationFormValues>({ onAccept: sendAccept });
  const requestAcceptFeeRef = useRef<
    ((values: NegotiationFormValues, notice: string | null) => Promise<void>) | null
  >(null);
  useEffect(() => {
    if (!acceptRefusal) return;
    const isFresh = acceptRefusal.at >= mountedAtRef.current;
    const isThisResponse =
      acceptRefusal.responseId === undefined ||
      acceptRefusal.responseId === (parsedResponseId ?? machineResponseId);
    negotiationRef?.send({
      type: 'ON_CONSUME_ACCEPT_REFUSAL',
      payload: { at: acceptRefusal.at },
    });
    const values = lastAcceptValuesRef.current;
    if (!isFresh || !isThisResponse || !values) return;
    void requestAcceptFeeRef.current?.(
      values,
      acceptRefusal.code === 'FEE_SCHEDULE_CHANGED'
        ? FEE_SCHEDULE_CHANGED_MESSAGE
        : FEE_AUTHORIZATION_REQUIRED_MESSAGE
    );
  }, [acceptRefusal, negotiationRef, parsedResponseId, machineResponseId]);

  const resolver = useMemo(
    () => zodResolver(buildNegotiationSchema(parsedResponseType)),
    [parsedResponseType]
  );

  const methods = useCustomForm<NegotiationFormValues>({
    // @ts-expect-error typing for preprocess parsing does not work well
    resolver,
    defaultValues: (draft?.terms ?? negotiation) as NegotiationFormValues | undefined,
  });

  const {
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = methods;

  const formVals = watch();
  const baseline = draft?.terms ?? negotiation;

  useEffect(() => {
    let changedFields = 0;
    Object.keys(formVals).forEach((field) => {
      const formVal = getValues(field as keyof NegotiationSchema);
      const baselineVal = baseline?.[field as keyof NegotiationSchema];
      if (
        (!field.includes('Formatted') &&
          String(formVal) !== String(baselineVal) &&
          Number(formVal) !== Number(baselineVal)) ||
        (formVal as { value: string })?.value !==
          (
            baselineVal as {
              value: string;
            }
          )?.value
      ) {
        changedFields++;
      }
    });
    setNumChangedFields(changedFields);
  }, [formVals]);

  // Reset the form from the draft's terms when the draft object identity
  // changes, and from `negotiation` when THAT changes -- never on a 400,
  // which leaves both untouched so the user's edits survive.
  useEffect(() => {
    if (negotiationRef?.getSnapshot()?.context.loading) return;
    if (draft) {
      methods.reset(draft.terms as NegotiationFormValues);
      setFormReset(true);
      return;
    }
    if (!negotiation) {
      // Empty chain (brand-new MSaaS JobResponse, no v1 yet, and no draft
      // requested), confirmed after the load actually settled.
      setFormReset(true);
      return;
    }
    methods.reset(negotiation);
    setFormReset(true);
    // negotiationLoading is intentionally a dependency even though the effect
    // body re-reads live state: it's what re-triggers this effect once a
    // load that resolved to a still-empty chain (negotiation reference
    // unchanged) finishes, so formReset still gets resolved in that case.
  }, [draft, negotiation, negotiationLoading, negotiationRef]);

  useEffect(() => {
    if (!negotiationRef) return;
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab) {
      negotiationRef.send({ type: 'ON_SET_TAB_INDEX', payload: { index: Number(tab) } });
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('tab', tabIndex.toString());
    window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
  }, [tabIndex]);

  // Missing responseId AND no draft source: show a clear error instead of
  // silently loading whatever the machine happens to have.
  if (parsedResponseId === undefined && machineResponseId === undefined && !draftSource && !draft) {
    return (
      <div
        className="flex items-start gap-3 p-4 my-6 bg-error-t-900 rounded-lg"
        data-cy={TAGS.layout}
      >
        <ExclamationCircleIcon className="h-5 w-5 text-error mt-0.5" />
        <div>
          <p className="body-text text-error font-medium">Missing responseId</p>
          <p className="body-text text-error">
            This negotiation editor requires a response id. Please open the editor from the
            opportunities page.
          </p>
        </div>
      </div>
    );
  }

  if (
    submitLifecycleBusy &&
    !draftScopeMatches &&
    !(responseScopeMatches && employerScopeMatches)
  ) {
    return (
      <div className="flex flex-col items-center gap-4 p-6" role="status">
        <LoadingSpinner />
        <p>Another offer is being submitted. This negotiation will load when it finishes.</p>
      </div>
    );
  }

  const mode = getNegotiationViewMode({
    loading:
      bootstrapLoading ||
      negotiationLoading ||
      !formReset ||
      (!!machineDraft && !draftScopeMatches),
    loadFailed: negotiationLoadFailed,
    draft,
    submitting,
    negotiation,
    latestVersion,
    isReadOnly,
  });

  if (mode === 'loading') {
    return (
      <div className="flex justify-center items-center mt-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!negotiationRef) return null;

  // [MCV-1557] A failed load also leaves `negotiation` undefined, but telling
  // the user "no negotiation yet" would be a lie that invites them to start a
  // duplicate one. ON_LOAD clears the previous response's chain, so the only
  // alternative here would be rendering the PREVIOUS response's negotiation
  // under this one — which is exactly the bug this state exists to prevent.
  if (mode === 'error') {
    return (
      <div
        className="flex items-start gap-3 p-4 my-6 bg-error-t-900 rounded-lg"
        data-cy={TAGS.layout}
      >
        <InformationCircleIcon className="h-5 w-5 text-error-50 mt-0.5" />
        <div>
          <p className="body-text text-error-50 font-medium">Could not load this negotiation</p>
          <p className="body-text">
            Please reopen the opportunity to check its current state before trying again.
          </p>
          {uncertainSubmission && (
            <Button
              type="button"
              label="Retry original offer"
              onClick={() => negotiationRef.send({ type: 'ON_RETRY_SUBMIT_DRAFT' })}
            />
          )}
        </div>
      </div>
    );
  }

  // Empty chain, only reachable when startDraftIfEmpty is false (or there is
  // genuinely no draft to open): getByResponseId returns [] until the first
  // counter is posted. Show a clear instructional state so the UI does not
  // render a blank page.
  if (mode === 'empty') {
    return (
      <div
        className="flex items-start gap-3 p-4 my-6 bg-warning-t-900 rounded-lg"
        data-cy={TAGS.layout}
      >
        <InformationCircleIcon className="h-5 w-5 text-warning-500 mt-0.5" />
        <div>
          <p className="body-text text-warning-500 font-medium">
            No negotiation yet for this response
          </p>
          <p className="body-text">
            Start or counter from the opportunities page to create the first negotiation.
          </p>
        </div>
      </div>
    );
  }

  const isDraftMode = mode === 'draft' || mode === 'submitting';
  const activeTab = TABS[tabIndex];

  const focusAllocationErrorIfAny = () => {
    // [MCV-1645] Computed directly via the same pure helper the resolver uses,
    // rather than read off `methods.formState.errors` (destructured at render
    // time, and RHF does not guarantee `formState` reflects a just-awaited
    // `trigger()` synchronously) -- this is deterministic and needs no extra
    // tick to observe.
    const allocationError =
      parsedResponseType === 'msaas'
        ? getShiftAllocationError(methods.getValues() as unknown as Record<string, unknown>)
        : null;
    if (!allocationError) {
      return false;
    }
    negotiationRef.send({
      type: 'ON_SET_TAB_INDEX',
      payload: { index: TABS.findIndex((tab) => tab.name === 'Shift Distribution') },
    });
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(
        '[data-cy="negotiation-shift-allocation-error"]'
      );
      el?.focus();
    });
    return true;
  };

  const handleSubmitOffer = async () => {
    const valid = await trigger();
    if (!valid) {
      focusAllocationErrorIfAny();
      return;
    }
    if (!draft) return;
    negotiationRef.send({
      type: 'ON_SUBMIT_DRAFT',
      data: { ...draft.terms, ...formVals } as NegotiationFormValues,
      providerIds: draftProviderIds,
    });
  };

  const handleCounterClick = async () => {
    const valid = await trigger();
    if (!valid) {
      focusAllocationErrorIfAny();
      return;
    }
    if (negotiation) {
      negotiationRef.send({
        type: 'ON_COUNTER_OFFER',
        data: { ...negotiation, ...formVals } as NegotiationFormValues,
      });
    }
  };

  const handleAcceptClick = async () => {
    // Accepting a regular JOB offer posts only the JobResponse id; edited form
    // terms are ignored by the API. Legacy offers can contain nullable values
    // that strict draft/counter validation rightly rejects, so they must not
    // prevent accepting the persisted offer exactly as it stands.
    if (parsedResponseType !== 'job') {
      const valid = await trigger();
      if (!valid) {
        focusAllocationErrorIfAny();
        return;
      }
    }
    if (negotiation) {
      const acceptedValues =
        parsedResponseType === 'job'
          ? negotiation
          : ({ ...negotiation, ...formVals } as NegotiationFormValues);
      await requestAcceptFee(acceptedValues, null);
    }
  };

  // Previews the activation fee (and, for B2B, the platform-fee schedule) and opens the dialog when
  // this viewer is the payer; otherwise accepts straight away. A failed preview accepts nothing:
  // nothing may be accepted without the employer having seen the fee.
  const requestAcceptFee = async (values: NegotiationFormValues, notice: string | null) => {
    const jobResponseId = values.jobResponse?.id;
    if (!jobResponseId || currentViewerRole !== 'EMPLOYER') {
      // A refused fee authorization never falls through to a bare accept.
      if (notice == null) sendAccept(values);
      return;
    }
    await acceptFee.requestAccept(jobResponseId, values, notice);
  };

  requestAcceptFeeRef.current = requestAcceptFee;

  const handleCloseClick = () => {
    if (negotiationRef.getSnapshot().context.submitLifecycleBusy) return;
    negotiationRef.send({ type: 'ON_CLOSE_OFFER' });
    onClose?.();
  };

  const listingTitle = draft
    ? draft.listingTitle
    : negotiation
      ? getNegotiationListingTitle(negotiation)
      : undefined;
  const listingOwnerName = draft
    ? draft.listingOwnerName
    : negotiation
      ? getNegotiationListingOwnerName(negotiation)
      : undefined;
  const responderName = draft
    ? draft.responderName
    : negotiation
      ? getNegotiationResponderName(negotiation)
      : undefined;
  const isLatestVersion = negotiation?.version === latestVersion;
  const awaitingCurrentViewer =
    !isDraftMode && negotiation && isLatestVersion
      ? isNegotiationAwaitingViewer(negotiation, {
          employerUser,
          provider,
          viewerRole: currentViewerRole,
        })
      : false;
  const turnLabel = getNegotiationTurnLabel({
    awaitingCurrentViewer,
    isLatestVersion,
    isReadOnly,
    isDraft: mode === 'draft',
    isSubmitting: mode === 'submitting',
  });

  const inputsDisabled = (mode !== 'draft' && mode !== 'actionable') || listingFilled;
  const sideCopy = b2bSideCopy(listingSide);

  return (
    <NegotiationRefContext.Provider value={negotiationRef}>
      <NegotiationEditingContext.Provider
        value={{ inputsDisabled, responseType: parsedResponseType }}
      >
        <FormProvider {...methods}>
          <div className="mb-12">
            <div className="text-left my-6">
              <div className="flex flex-row justify-between" data-cy={TAGS.layout}>
                <h2 className="sub-heading-sb mb-3">
                  {parsedResponseType === 'job'
                    ? 'Job Contract Negotiation'
                    : 'B2B Contract Negotiation'}
                </h2>
                <span
                  className="inline-flex items-center self-start rounded-full bg-warning-500 px-3 py-1 text-sm font-medium text-white"
                  data-cy="negotiation-version-status"
                >
                  {isDraftMode ? turnLabel : `Version ${negotiation?.version} • ${turnLabel}`}
                </span>
              </div>
              <p className="body-text"> {listingTitle}</p>
            </div>
            <div className="flex flex-row gap-2 justify-between bg-grey-900 p-4 rounded-lg mb-4">
              <div className="justify-start">
                <div className="flex flex-row gap-2">
                  <BuildingOffice2Icon className="h-5 w-5 text-grey-100 justify-center my-auto" />
                  <div className="body-text text-left align-items-center">
                    <p className="pb-2 text-grey-400">{sideCopy.negotiationListingParty}</p>
                    <p>{listingOwnerName}</p>
                  </div>
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 text-grey-100 my-auto" />
              <div className="justify-end">
                <div className="flex flex-row gap-2">
                  <div className="body-text text-left align-items-center">
                    <p className="pb-2 text-grey-400">{sideCopy.negotiationRespondingParty}</p>
                    <p>{responderName}</p>
                  </div>
                  <UserGroupIcon className="h-5 w-5 text-grey-100 justify-center my-auto" />
                </div>
              </div>
            </div>
            {listingFilled && <ListingFilledNotice className="mb-4" />}
            {/* [MCV-1954] Both parties accepted: the contract is waiting on the Contracts page. */}
            {negotiation?.jobResponse?.status === 'ACCEPTED' && (
              <div className="mb-4">
                <ContractReadyNotice />
              </div>
            )}
            {!isDraftMode && (
              <div className="flex flex-row mb-4">
                <InformationCircleIcon className="h-5 w-5 text-warning-500 my-auto" />
                <p className="body-text text-left my-auto ml-2 text-warning-500">
                  {numChangedFields} fields modified
                </p>
                <p className="body-text text-left my-auto ml-2">from previous terms</p>
              </div>
            )}
            <Tabs
              tabs={TABS}
              handleTabChange={async (index: number) => {
                if (index === tabIndex) return;
                // [MCV-1645] Shift Distribution must always be reachable so the
                // allocation error (a `root.shiftAllocation` error, not tied to
                // any single field) can be seen and fixed -- field-level
                // validation still gates the OTHER tabs.
                const targetsShiftDistribution = TABS[index]?.name === 'Shift Distribution';
                const valid = targetsShiftDistribution || (await trigger());
                if (valid) {
                  negotiationRef.send({ type: 'ON_SET_TAB_INDEX', payload: { index } });
                }
              }}
              activeTab={tabIndex}
            />
            {Object.keys(errors).length > 0 && (
              <div className="flex flex-row gap-2 mb-4 my-4 bg-error-t-900 p-4 rounded-lg">
                <ExclamationCircleIcon className="h-5 w-5 text-error my-auto" />
                <p className="body-text text-left my-auto text-error">
                  There are form errors. Please fix them before proceeding.
                </p>
              </div>
            )}
            {/* The enclosing modal/page is the sole vertical scroll owner. An
                inner capped scroller here trapped tall Shift Distribution content
                above the footer and created a second scrollbar. */}
            <fieldset disabled={inputsDisabled} className="min-w-0 border-0 p-0 m-0">
              <div data-cy="negotiation-tab-content" className="min-w-0">
                {activeTab.name === 'Contract Overview' && (
                  <ContractOverview listingSide={listingSide} />
                )}
                {activeTab.name === 'Shift Distribution' && <ShiftDistribution />}
                {activeTab.name === 'Compensation' && <Compensation />}
                {activeTab.name === 'Contract Terms' && <ContractTerms />}
              </div>
            </fieldset>

            <p
              role="status"
              aria-live="polite"
              className={uncertainSubmission ? 'body-text mt-4' : undefined}
            >
              {uncertainSubmission
                ? "We couldn't confirm whether your offer completed. Retry sends the original offer, not any edits made afterward. An existing offer will be opened, not replaced."
                : ''}
            </p>
            <div className="flex gap-4 mt-6 pt-5 border-t-2 border-grey-900">
              <Button
                colorVariant="tertiary"
                type="button"
                cyTag={TAGS.formBtns.closeBtn}
                label="Close"
                onClick={handleCloseClick}
                disabled={submitLifecycleBusy}
              />
              <div className="flex gap-4 ml-auto">
                {listingFilled ? null : isDraftMode ? (
                  <Button
                    type="button"
                    label={
                      mode === 'submitting'
                        ? (submitLabels?.submitting ?? 'Submitting Offer')
                        : uncertainSubmission
                          ? (submitLabels?.retry ?? 'Retry original offer')
                          : (submitLabels?.submit ?? 'Submit Offer')
                    }
                    onClick={
                      uncertainSubmission
                        ? () => negotiationRef.send({ type: 'ON_RETRY_SUBMIT_DRAFT' })
                        : handleSubmitOffer
                    }
                    disabled={mode === 'submitting'}
                    cyTag={SUBMIT_OFFER_BTN_CY_TAG}
                  />
                ) : mode === 'history' ? (
                  <div className="flex items-center gap-2 text-grey-400">
                    <InformationCircleIcon className="h-5 w-5" />
                    <p className="body-text">This is not the latest version of the negotiation</p>
                  </div>
                ) : mode === 'waiting' ? (
                  <div className="flex items-center gap-2 text-grey-400">
                    <InformationCircleIcon className="h-5 w-5" />
                    <p className="body-text">Awaiting the other party's response</p>
                  </div>
                ) : (
                  <>
                    {viewerIsListingOwner ? (
                      <Button
                        colorVariant="error"
                        type="button"
                        cyTag={TAGS.formBtns.declineBtn}
                        label="Decline"
                        onClick={() =>
                          confirmationView.send(
                            declineResponseConfirmation(() =>
                              negotiationRef.send({
                                type: 'ON_REJECT_OFFER',
                                payload: { id: negotiation?.id.toString() || '-1' },
                              })
                            )
                          )
                        }
                      />
                    ) : (
                      <Button
                        colorVariant="error"
                        type="button"
                        cyTag={TAGS.formBtns.withdrawBtn}
                        label="Withdraw"
                        onClick={() =>
                          confirmationView.send(
                            withdrawResponseConfirmation(() =>
                              negotiationRef.send({
                                type: 'ON_WITHDRAW_OFFER',
                                payload: { id: negotiation?.id.toString() || '-1' },
                              })
                            )
                          )
                        }
                      />
                    )}
                    {numChangedFields > 0 ? (
                      <Button
                        colorVariant="secondary"
                        type="button"
                        cyTag={TAGS.formBtns.counterBtn}
                        label="Counter Offer"
                        onClick={handleCounterClick}
                      />
                    ) : (
                      <Button
                        type="button"
                        label="Accept Offer"
                        onClick={handleAcceptClick}
                        cyTag={TAGS.formBtns.acceptBtn}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {footer}
          <ActivationFeeDialog {...acceptFee.dialogProps} />
        </FormProvider>
      </NegotiationEditingContext.Provider>
    </NegotiationRefContext.Provider>
  );
};
